import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  StyleSheet,
  ScrollView,
  Linking,
} from 'react-native';
import {
  Searchbar,
  Chip,
  Card,
  Button,
  Switch,
  useTheme,
  Title,
  Paragraph,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService from '../services/ApiService';
import FilterChip from '../components/FilterChip';

interface Rum {
  id: number;
  name: string;
  brand?: string;
  distillery_id?: number;
  distillery_name?: string;
  distillery_country?: string;
  distillery_region?: string;
  country?: string;
  region?: string;
  // Comprehensive schema fields
  style?: string;
  base_material?: string;
  color_added_flag?: string;
  abv?: number;
  overproof_flag?: boolean;
  age_statement_years?: number;
  // Legacy fields (for backward compatibility)
  type?: string;
  age_statement?: string;
  proof?: number;
  price_msrp?: number | string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  source_url?: string;
}

interface FilterGroup {
  type: 'country' | 'region' | 'distillery' | 'type' | 'price_range';
  selected: string[];
  logic: 'AND' | 'OR';
}

const RumsScreen: React.FC<{ navigation: any }> = ({ navigation }) => {
  const theme = useTheme();
  const [rums, setRums] = useState<Rum[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [distilleries, setDistilleries] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [priceRanges, setPriceRanges] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<{
    country: number;
    region: number;
    distillery: number;
    type: number;
    price_range: number;
  }>({
    country: 12,
    region: 6,
    distillery: 12,
    type: 8,
    price_range: 4,
  });
  const [filters, setFilters] = useState<FilterGroup[]>([
    { type: 'country', selected: [], logic: 'OR' },
    { type: 'region', selected: [], logic: 'OR' },
    { type: 'distillery', selected: [], logic: 'OR' },
    { type: 'type', selected: [], logic: 'OR' },
    { type: 'price_range', selected: [], logic: 'OR' }
  ]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load rums data
      const rumsData = await apiService.getRums();
      console.log('🔍 Rums data received:', rumsData);
      console.log('🔍 Rums data type:', typeof rumsData);
      console.log('🔍 Rums data is array:', Array.isArray(rumsData));
      
      // Ensure rumsData is an array
      const rumsArray = Array.isArray(rumsData) ? rumsData : [];
      setRums(rumsArray);
      
      // Extract unique values for filters from rums data
      const uniqueCountries = Array.from(new Set(rumsArray.map(rum => rum.country).filter((country): country is string => Boolean(country))));
      const uniqueRegions = Array.from(new Set(rumsArray.map(rum => rum.region).filter((region): region is string => Boolean(region))));
      const uniqueDistilleries = Array.from(new Set(rumsArray.map(rum => rum.distillery_name).filter((distillery): distillery is string => Boolean(distillery))));
      // Use base_material if available, fallback to type
      const uniqueTypes = Array.from(new Set(rumsArray.map(rum => rum.base_material || rum.type).filter((type): type is string => Boolean(type))));
      
      setCountries(uniqueCountries.sort());
      setRegions(uniqueRegions.sort());
      setDistilleries(uniqueDistilleries.sort());
      setTypes(uniqueTypes.sort());
      
      // Generate price ranges based on available data
      const priceRanges = ['Under $30', '$30-$50', '$50-$100', 'Over $100'];
      setPriceRanges(priceRanges);
      
    } catch (error) {
      console.error('Error loading rums data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle initial filters from navigation params
  useEffect(() => {
    if (navigation.getState().routes.length > 0) {
      const currentRoute = navigation.getState().routes[navigation.getState().routes.length - 1];
      if (currentRoute.params?.initialFilters) {
        const { initialFilters } = currentRoute.params;
        
        // Apply initial filters
        const newFilters = filters.map(filter => {
          if (initialFilters[filter.type]) {
            return {
              ...filter,
              selected: initialFilters[filter.type]
            };
          }
          return filter;
        });
        
        setFilters(newFilters);
      }
    }
  }, [navigation]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, []);

  const toggleFilter = (filterType: FilterGroup['type'], value: string) => {
    setFilters(prevFilters => 
      prevFilters.map(filter => 
        filter.type === filterType 
          ? {
              ...filter,
              selected: filter.selected.includes(value)
                ? filter.selected.filter(item => item !== value)
                : [...filter.selected, value]
            }
          : filter
      )
    );
  };

  const getAllSelectedFilters = () => {
    return filters.reduce((acc, filter) => acc.concat(filter.selected), [] as string[]);
  };

  const filteredRums = useMemo(() => {
    let filtered = rums;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(rum => 
        rum.name.toLowerCase().includes(query) ||
        (rum.distillery_name && rum.distillery_name.toLowerCase().includes(query)) ||
        (rum.country && rum.country.toLowerCase().includes(query)) ||
        (rum.region && rum.region.toLowerCase().includes(query)) ||
        (rum.description && rum.description.toLowerCase().includes(query))
      );
    }

    // Apply filters
    filters.forEach(filterGroup => {
      if (filterGroup.selected.length > 0) {
        filtered = filtered.filter(rum => {
          let rumValue: string | undefined;
          switch (filterGroup.type) {
            case 'country':
              rumValue = rum.country;
              break;
            case 'region':
              rumValue = rum.region;
              break;
            case 'distillery':
              rumValue = rum.distillery_name;
              break;
            case 'type':
              rumValue = rum.base_material || rum.type;
              break;
            case 'price_range':
              // Use price_msrp if available, otherwise use ABV-based ranges
              if (rum.price_msrp) {
                const price = typeof rum.price_msrp === 'string' ? parseFloat(rum.price_msrp) : rum.price_msrp;
                if (price < 30) rumValue = 'Under $30';
                else if (price >= 30 && price < 50) rumValue = '$30-$50';
                else if (price >= 50 && price < 100) rumValue = '$50-$100';
                else if (price >= 100) rumValue = 'Over $100';
              } else if (rum.abv) {
                // Fallback to ABV-based ranges
                if (rum.abv < 40) rumValue = 'Under 40% ABV';
                else if (rum.abv >= 40 && rum.abv <= 50) rumValue = '40-50% ABV';
                else if (rum.abv > 50) rumValue = 'Over 50% ABV';
              } else if (rum.proof) {
                // Legacy proof-based ranges
                if (rum.proof < 80) rumValue = 'Under 80 proof';
                else if (rum.proof >= 80 && rum.proof <= 100) rumValue = '80-100 proof';
                else if (rum.proof > 100) rumValue = 'Over 100 proof';
              }
              break;
            default:
              return true;
          }
          return rumValue && filterGroup.selected.includes(rumValue);
        });
      }
    });

    return filtered;
  }, [rums, searchQuery, filters]);

  const openTotalWine = (rumName: string) => {
    // For now, we'll use the improved search approach
    // In the future, this could check for stored source_url in the rum object
    const searchQuery = generateTotalWineSearchQuery(rumName);
    const url = `https://www.totalwine.com/search/all?text=${encodeURIComponent(searchQuery)}`;
    Linking.openURL(url);
  };

  const generateTotalWineSearchQuery = (rumName: string) => {
    // Remove diacritics for Total Wine search
    const removeDiacritics = (str: string) => {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    };
    
    const cleanName = removeDiacritics(rumName);
    
    // Clean up extra spaces and special characters
    let query = cleanName.replace(/\s+/g, ' ').trim();
    
    return query;
  };

  const renderRum = ({ item }: { item: Rum }) => (
    <TouchableOpacity onPress={() => navigation.navigate('RumDetail', { rum: item })}>
      <Card style={[styles.rumCard, { backgroundColor: theme.colors.surface }]}>
        <Card.Content style={styles.rumContent}>
          {/* Line 1: Rum Name */}
          <Title style={[styles.rumTitle, { color: theme.colors.onSurface }]}>
            {item.name}
          </Title>

          {/* Line 2: Filter Chips - Country, Region, Distillery */}
          <View style={styles.filterChipsRow}>
            {item.country && item.country !== 'Unknown' && item.country !== 'Coming soon' && (
              <FilterChip
                label={item.country}
                selected={filters.find(f => f.type === 'country')?.selected.includes(item.country) || false}
                onPress={() => toggleFilter('country', item.country || '')}
                size="small"
              />
            )}
            {item.region && item.region !== 'Unknown' && item.region !== 'Coming soon' && (
              <FilterChip
                label={item.region}
                selected={filters.find(f => f.type === 'region')?.selected.includes(item.region) || false}
                onPress={() => toggleFilter('region', item.region || '')}
                size="small"
              />
            )}
            {item.distillery_name && item.distillery_name !== 'Unknown' && item.distillery_name !== 'Coming soon' && (
              <FilterChip
                label={item.distillery_name}
                selected={filters.find(f => f.type === 'distillery')?.selected.includes(item.distillery_name) || false}
                onPress={() => toggleFilter('distillery', item.distillery_name || '')}
                size="small"
              />
            )}
          </View>

          {/* Line 3: Type, Age, ABV */}
          <View style={styles.detailsRow}>
            {(item.base_material || item.type) && (item.base_material || item.type) !== 'Unknown' && (item.base_material || item.type) !== 'Coming soon' && (
              <View style={styles.detailItem}>
                <Text style={[styles.detailLabel, { color: theme.colors.primary }]}>Type:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {item.base_material || item.type}
                </Text>
              </View>
            )}
            {(item.age_statement_years || item.age_statement) && (
              <View style={styles.detailItem}>
                <Text style={[styles.detailLabel, { color: theme.colors.primary }]}>Age:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {item.age_statement_years 
                    ? `${item.age_statement_years} ${item.age_statement_years === 1 ? 'year' : 'years'}`
                    : item.age_statement}
                </Text>
              </View>
            )}
            {(item.abv || item.proof) && (
              <View style={styles.detailItem}>
                <Text style={[styles.detailLabel, { color: theme.colors.primary }]}>ABV:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.onSurface }]}>
                  {item.abv ? `${item.abv}%` : item.proof ? `${item.proof} proof` : ''}
                </Text>
              </View>
            )}
          </View>

          {/* Line 4+: Description (3 lines) */}
          {item.description && item.description !== 'Description coming soon...' && item.description !== 'Coming soon' && (
            <View style={styles.descriptionContainer}>
              <Text style={[styles.descriptionLabel, { color: theme.colors.primary }]}>Description:</Text>
              <Text style={[styles.descriptionText, { color: theme.colors.onSurfaceVariant }]} numberOfLines={3}>
                {item.description}
              </Text>
            </View>
          )}

          {/* Additional Details Row */}
          <View style={styles.additionalDetailsRow}>
            {/* Additional details can be added here */}
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.totalWineButton, { backgroundColor: '#1a4d80' }]}
              onPress={() => openTotalWine(item.name)}
            >
              <Ionicons name="wine" size={16} color="#fff" />
              <Text style={[styles.buttonText, { color: '#fff' }]}>
                Total Wine
              </Text>
            </TouchableOpacity>
          </View>
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: theme.colors.background }]}>
        <Text style={{ color: theme.colors.onBackground }}>Loading rums...</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <Title style={styles.headerTitle}>
          🥃 Rum Database
        </Title>
        <Paragraph style={styles.headerSubtitle}>
          Discover {filteredRums.length} rum brands from around the world
        </Paragraph>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder="Search rums..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchBar}
          inputStyle={{ color: theme.colors.onSurface }}
        />
      </View>

      {/* Filter Section */}
      <View style={styles.filtersContainer}>
        <View style={styles.filterHeader}>
          <Text style={styles.filterTitle}>Filters</Text>
          <TouchableOpacity
            style={styles.collapseButton}
            onPress={() => setFiltersCollapsed(!filtersCollapsed)}
          >
            <Text style={{ color: theme.colors.primary }}>
              {filtersCollapsed ? 'Expand' : 'Collapse'}
            </Text>
          </TouchableOpacity>
        </View>

        {getAllSelectedFilters().length > 0 && !filtersCollapsed && (
          <View style={styles.selectedFiltersContainer}>
            <View style={styles.selectedFiltersLabel}>
              <Text style={styles.selectedFiltersLabelText}>
                Selected filters ({getAllSelectedFilters().length}):
              </Text>
            </View>
            <ScrollView
              horizontal
              style={styles.selectedFiltersScroll}
              showsHorizontalScrollIndicator={false}
            >
              <View style={styles.selectedFiltersChips}>
                {getAllSelectedFilters().map((filter) => (
                  <FilterChip
                    key={filter}
                    label={filter}
                    selected={true}
                    onPress={() => {
                      filters.forEach(filterGroup => {
                        if (filterGroup.selected.includes(filter)) {
                          toggleFilter(filterGroup.type, filter);
                        }
                      });
                    }}
                    size="small"
                  />
                ))}
              </View>
            </ScrollView>
          </View>
        )}

        {!filtersCollapsed && (
          <ScrollView style={styles.filtersScroll} showsVerticalScrollIndicator={false}>
            {/* Countries */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Countries</Text>
              <View style={styles.filterChips}>
                {countries.slice(0, expandedFilters.country).map((country) => (
                  <FilterChip
                    key={country}
                    label={country}
                    selected={filters.find(f => f.type === 'country')?.selected.includes(country) || false}
                    onPress={() => toggleFilter('country', country)}
                    size="small"
                  />
                ))}
                {countries.length > expandedFilters.country && (
                  <Button
                    mode="text"
                    onPress={() => setExpandedFilters(prev => ({ ...prev, country: prev.country + 12 }))}
                    style={styles.expandButton}
                  >
                    Show More
                  </Button>
                )}
              </View>
            </View>

            {/* Regions */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Regions</Text>
              <View style={styles.filterChips}>
                {regions.slice(0, expandedFilters.region).map((region) => (
                  <FilterChip
                    key={region}
                    label={region}
                    selected={filters.find(f => f.type === 'region')?.selected.includes(region) || false}
                    onPress={() => toggleFilter('region', region)}
                    size="small"
                  />
                ))}
                {regions.length > expandedFilters.region && (
                  <Button
                    mode="text"
                    onPress={() => setExpandedFilters(prev => ({ ...prev, region: prev.region + 6 }))}
                    style={styles.expandButton}
                  >
                    Show More
                  </Button>
                )}
              </View>
            </View>

            {/* Distilleries */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Distilleries</Text>
              <View style={styles.filterChips}>
                {distilleries.slice(0, expandedFilters.distillery).map((distillery) => (
                  <FilterChip
                    key={distillery}
                    label={distillery}
                    selected={filters.find(f => f.type === 'distillery')?.selected.includes(distillery) || false}
                    onPress={() => toggleFilter('distillery', distillery)}
                    size="small"
                  />
                ))}
                {distilleries.length > expandedFilters.distillery && (
                  <Button
                    mode="text"
                    onPress={() => setExpandedFilters(prev => ({ ...prev, distillery: prev.distillery + 12 }))}
                    style={styles.expandButton}
                  >
                    Show More
                  </Button>
                )}
              </View>
            </View>

            {/* Types */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Types</Text>
              <View style={styles.filterChips}>
                {types.slice(0, expandedFilters.type).map((type) => (
                  <FilterChip
                    key={type}
                    label={type}
                    selected={filters.find(f => f.type === 'type')?.selected.includes(type) || false}
                    onPress={() => toggleFilter('type', type)}
                    size="small"
                  />
                ))}
                {types.length > expandedFilters.type && (
                  <Button
                    mode="text"
                    onPress={() => setExpandedFilters(prev => ({ ...prev, type: prev.type + 8 }))}
                    style={styles.expandButton}
                  >
                    Show More
                  </Button>
                )}
              </View>
            </View>

            {/* Price Ranges */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Price Ranges</Text>
              <View style={styles.filterChips}>
                {priceRanges.slice(0, expandedFilters.price_range).map((priceRange) => (
                  <FilterChip
                    key={priceRange}
                    label={priceRange}
                    selected={filters.find(f => f.type === 'price_range')?.selected.includes(priceRange) || false}
                    onPress={() => toggleFilter('price_range', priceRange)}
                    size="small"
                  />
                ))}
                {priceRanges.length > expandedFilters.price_range && (
                  <Button
                    mode="text"
                    onPress={() => setExpandedFilters(prev => ({ ...prev, price_range: prev.price_range + 4 }))}
                    style={styles.expandButton}
                  >
                    Show More
                  </Button>
                )}
              </View>
            </View>
          </ScrollView>
        )}
      </View>

      {/* Rums List */}
      <FlatList
        data={filteredRums}
        renderItem={renderRum}
        keyExtractor={(item) => item.id.toString()}
        style={styles.rumsList}
        contentContainerStyle={styles.rumsListContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    opacity: 0.7,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  searchBar: {
    elevation: 2,
  },
  filtersContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  filterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  collapseButton: {
    padding: 4,
  },
  selectedFiltersContainer: {
    marginBottom: 8,
  },
  selectedFiltersLabel: {
    marginBottom: 4,
  },
  selectedFiltersLabelText: {
    fontSize: 12,
    opacity: 0.7,
  },
  selectedFiltersScroll: {
    maxHeight: 40,
  },
  selectedFiltersChips: {
    flexDirection: 'row',
    gap: 8,
  },
  filtersScroll: {
    maxHeight: 200,
  },
  filterSection: {
    marginBottom: 12,
  },
  filterSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  filterChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  expandButton: {
    marginTop: 4,
  },
  rumsList: {
    flex: 1,
  },
  rumsListContent: {
    padding: 16,
    paddingTop: 8,
  },
  rumCard: {
    marginBottom: 16,
    elevation: 2,
  },
  rumContent: {
    padding: 16,
  },
  rumTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  filterChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  detailsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  detailValue: {
    fontSize: 12,
  },
  descriptionContainer: {
    marginBottom: 8,
  },
  descriptionLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 2,
  },
  descriptionText: {
    fontSize: 12,
    lineHeight: 16,
  },
  additionalDetailsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  totalWineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 4,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 4,
    flex: 1,
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default RumsScreen;